import axios from 'axios';

// Backend base URL.
// - Development: leave REACT_APP_API_URL unset — CRA proxy (package.json) forwards to localhost:5000.
// - Production:  set REACT_APP_API_URL in .env.production before `npm run build`,
//   e.g. REACT_APP_API_URL=https://api.yourdomain.lk
//   Leave it unset if the frontend and backend are served from the SAME domain.
export const API_BASE = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: `${API_BASE}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default api;

// ── Auth ─────────────────────────────────────────────────────────────────────
export const authAPI = {
  login:          (d) => api.post('/auth/login', d),
  register:       (d) => api.post('/auth/register', d),
  getMe:          ()  => api.get('/auth/me'),
  changePassword: (d) => api.put('/auth/password', d),
};

// ── Dashboard ─────────────────────────────────────────────────────────────────
export const dashboardAPI = {
  getSummary: () => api.get('/dashboard'),
};

// ── Vehicles ──────────────────────────────────────────────────────────────────
export const vehicleAPI = {
  getAll:      (p)    => api.get('/vehicles', { params: p }),
  getOne:      (id)   => api.get(`/vehicles/${id}`),
  getStats:    ()     => api.get('/vehicles/stats'),
  getExpiring: (days) => api.get('/vehicles/expiring', { params: { days } }),
  create:      (d)    => api.post('/vehicles', d),
  update:      (id,d) => api.put(`/vehicles/${id}`, d),
  setStatus:   (id,status) => api.patch(`/vehicles/${id}/status`, { status }),
  leaseAlerts: ()     => api.get('/vehicles/lease-alerts'),
  remove:      (id)   => api.delete(`/vehicles/${id}`),
  uploadFiles: (id, formData) => api.post(`/vehicles/${id}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteImage: (id, filename) => api.delete(`/vehicles/${id}/images/${filename}`),
};

// ── Drivers ───────────────────────────────────────────────────────────────────
export const driverAPI = {
  getAll:            (p) => api.get('/drivers', { params: p }),
  getOne:            (id)=> api.get(`/drivers/${id}`),
  getExpiringLicenses:(p)=> api.get('/drivers/expiring-licenses', { params: p }),
  create:            (d) => api.post('/drivers', d),
  update:            (id,d)=> api.put(`/drivers/${id}`, d),
  remove:            (id)  => api.delete(`/drivers/${id}`),
  licenseAlerts:     ()   => api.get('/drivers/license-alerts'),
  uploadPhoto:       (id,file) => { const fd = new FormData(); fd.append('photo', file);
                        return api.post(`/drivers/${id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
};

// ── Assignments ───────────────────────────────────────────────────────────────
export const assignmentAPI = {
  getAll:        (p) => api.get('/assignments', { params: p }),
  create:        (d) => api.post('/assignments', d),
  endAssignment: (id,d)=> api.put(`/assignments/${id}/end`, d),
};

// ── Repairs ───────────────────────────────────────────────────────────────────
export const repairAPI = {
  getAll:   (p) => api.get('/repairs', { params: p }),
  getOne:   (id)=> api.get(`/repairs/${id}`),
  getStats: ()  => api.get('/repairs/stats'),
  create:   (d) => api.post('/repairs', d),
  update:   (id,d)=> api.put(`/repairs/${id}`, d),
  approve:  (id,d)=> api.post(`/repairs/${id}/approve`, d),
  managerReview: (id,d)=> api.post(`/repairs/${id}/manager-review`, d),
  complete: (id,d)=> api.post(`/repairs/${id}/complete`, d),
  remove:   (id) => api.delete(`/repairs/${id}`),
};

export const fuelCardAPI = {
  getAll: ()     => api.get('/fuel-cards'),
  create: (d)    => api.post('/fuel-cards', d),
  update: (id,d) => api.put(`/fuel-cards/${id}`, d),
};

export const lookupAPI = {
  getAll: (p)    => api.get('/lookups', { params: p }),
  create: (d)    => api.post('/lookups', d),
  update: (id,d) => api.put(`/lookups/${id}`, d),
  remove: (id)   => api.delete(`/lookups/${id}`),
};

export const companyAPI = {
  getAll: ()     => api.get('/companies'),
  create: (d)    => api.post('/companies', d),
  update: (id,d) => api.put(`/companies/${id}`, d),
};

export const partAPI = {
  getAll: (p)    => api.get('/parts', { params: p }),
  create: (d)    => api.post('/parts', d),
  update: (id,d) => api.put(`/parts/${id}`, d),
  remove: (id)   => api.delete(`/parts/${id}`),
  warrantyAlerts: () => api.get('/parts/warranty-alerts'),
};

export const accessoryAPI = {
  getAll: (p)    => api.get('/accessories', { params: p }),
  create: (d)    => api.post('/accessories', d),
  update: (id,d) => api.put(`/accessories/${id}`, d),
  remove: (id)   => api.delete(`/accessories/${id}`),
};

export const renewalAPI = {
  getAll:   (p)    => api.get('/renewals', { params: p }),
  getStats: (p)    => api.get('/renewals/stats', { params: p }),
  create:   (d)    => api.post('/renewals', d),
  update:   (id,d) => api.put(`/renewals/${id}`, d),
  remove:   (id)   => api.delete(`/renewals/${id}`),
};

export const accidentAPI = {
  getAll: (p)    => api.get('/accidents', { params: p }),
  create: (d)    => api.post('/accidents', d),
  update: (id,d) => api.put(`/accidents/${id}`, d),
  remove: (id)   => api.delete(`/accidents/${id}`),
  review: (id,d) => api.post(`/accidents/${id}/review`, d),
  managerReview: (id,d) => api.post(`/accidents/${id}/manager-review`, d),
};

export const invoiceAPI = {
  getAll: (p)    => api.get('/invoices', { params: p }),
  create: (d)    => api.post('/invoices', d),
  update: (id,d) => api.put(`/invoices/${id}`, d),
  remove: (id)   => api.delete(`/invoices/${id}`),
  review: (id,d) => api.post(`/invoices/${id}/review`, d),
  forward:(id)   => api.post(`/invoices/${id}/forward`),
  pay:    (id,d) => api.post(`/invoices/${id}/pay`, d),
};

// ── Fuel ──────────────────────────────────────────────────────────────────────
export const fuelAPI = {
  getAll:   (p) => api.get('/fuel', { params: p }),
  getStats: (p) => api.get('/fuel/stats', { params: p }),
  create:   (d) => api.post('/fuel', d),
};

// ── Compliance ────────────────────────────────────────────────────────────────
export const complianceAPI = {
  getAll:     (p) => api.get('/compliance', { params: p }),
  getExpiring:(p) => api.get('/compliance/expiring', { params: p }),
  create:     (d) => api.post('/compliance', d),
  update:     (id,d)=> api.put(`/compliance/${id}`, d),
};

// ── Inventory ─────────────────────────────────────────────────────────────────
export const inventoryAPI = {
  getAll:  (p) => api.get('/inventory', { params: p }),
  create:  (d) => api.post('/inventory', d),
  update:  (id,d)=> api.put(`/inventory/${id}`, d),
  remove:  (id)  => api.delete(`/inventory/${id}`),
};

// ── Garages ───────────────────────────────────────────────────────────────────
export const garageAPI = {
  getAll:  () => api.get('/garages'),
  create:  (d)=> api.post('/garages', d),
  update:  (id,d)=> api.put(`/garages/${id}`, d),
};

// ── Users ─────────────────────────────────────────────────────────────────────
export const referenceAPI = {
  getTyreSizes:    () => api.get('/reference/tyre-sizes'),
  getBatterySizes: () => api.get('/reference/battery-sizes'),
  getRepairTypes:  () => api.get('/reference/repair-types'),
  getServiceAreas: () => api.get('/reference/service-areas'),
  addRepairType:   (d) => api.post('/reference/repair-types', d),
  addServiceArea:  (d) => api.post('/reference/service-areas', d),
  addTyreSize:     (d) => api.post('/reference/tyre-sizes', d),
  addBatterySize:  (d) => api.post('/reference/battery-sizes', d),
  deleteRepairType:  (id) => api.delete(`/reference/repair-types/${id}`),
  deleteServiceArea: (id) => api.delete(`/reference/service-areas/${id}`),
};

export const branchAPI = {
  getAll: ()      => api.get('/branches'),
  create: (d)     => api.post('/branches', d),
  update: (id, d) => api.put(`/branches/${id}`, d),
  remove: (id)    => api.delete(`/branches/${id}`),
  upload: (id, fd)=> api.post(`/branches/${id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const userAPI = {
  getAll:  ()      => api.get('/users'),
  toggle:  (id, d) => api.put(`/users/${id}/toggle`, d || {}),
  update:  (id, d) => api.put(`/users/${id}`, d),
};
